import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import { useIntl, FormattedMessage } from "react-intl";
import SearchIcon from "@material-ui/icons/Search";
import Button from "@material-ui/core/Button";
import Alert from "@material-ui/lab/Alert";
import CircularProgress from "@material-ui/core/CircularProgress";
import Paper from "@material-ui/core/Paper";

import { FullWidthTextFieldWithSuffixIcon } from "../../../../components/full_width_text_field_with_suffix_icon/FullWidthTextFieldWithSuffixIcon";
import { BranchManagerService } from "../../../../services/BranchManagerService";
import { TeamMemberSearchResultItem } from "../../../../components/team_member_search_result_item/TeamMemberSearchResultItem";
import { EmployeeRecord } from "./components/employee_record/EmployeeRecord";
import { InviteEmployee } from "./modals/invite_employee/InviteEmployee";
import { MemberStatus } from "../../../../settings/MemberStatus";

interface MyTeamTabProps {
  branch: number;
}

const useStyles = makeStyles((theme) => ({
  leftSide: {
    height: "100%",
    overflow: "hidden",
    flexGrow: 1,
    paddingTop: 10,
    paddingBottom: 10,
    backgroundColor: theme.palette.common.white,
  },
  rightSide: {
    minHeight: "84.5vh",
    overflow: "hidden",
    flexGrow: 1,
    paddingTop: 20,
    paddingBottom: 10,
    backgroundColor: theme.palette.grey[200],
  },
  newButton: {
    textTransform: "none",
    fontSize: 13,
  },
  rightSideMainPart: {
    width: "100%",
    minHeight: "79vh",
    paddingTop: 10,
    paddingBottom: 10,
    paddingLeft: 20,
    paddingRight: 20,
  },
  rightSideMainGrid: {
    width: "100%",
    minHeight: "77vh",
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  inviteBody: {
    width: 416,
    padding: 10,
    border: "none",
  },
  divider: {
    marginTop: 10,
    marginBottom: 10,
  },
  inviteTitle: {
    fontSize: 20,
    fontWeight: "bold",
  },
  inviteLabel: {
    fontSize: 14,
  },
  actionButton: {
    margin: 10,
    textTransform: "none",
  },
}));

export const MyTeamTab = (props: MyTeamTabProps) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { branch } = props;
  const [employeeName, setEmployeeName] = useState("");
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingEmployee, setLoadingEmployee] = useState(false);
  const [errorOnLoadingEmployees, setErrorOnLoadingEmployees] = useState(false);
  const [errorOnLoadingEmployee, setErrorOnLoadingEmployee] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [inviteModalOpened, setInviteModalOpened] = useState(false);

  const handleEmployeeNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEmployeeName(event.target.value);
    setFilteredEmployees(
      employees.filter((employee) =>
        employee.name.toLowerCase().includes(event.target.value.toLowerCase())
      )
    );
  };

  const toggleInviteModal = () => setInviteModalOpened(!inviteModalOpened);

  const loadEmployees = async () => {
    setLoadingEmployees(true);
    setSelectedEmployee(null);
    setSelectedEmployeeId(null);
    setErrorOnLoadingEmployees(false);
    setEmployees([]);
    try {
      const employeesResult = await BranchManagerService.listEmployees({
        branchId: branch,
      });
      if (employeesResult.isSuccess && employeesResult.data.code === 100) {
        const responseData = JSON.parse(
          JSON.stringify(employeesResult.data.data)
        );
        let completeEmployees = responseData.employees.map((employee) => ({
          id: employee.id,
          name: employee.name,
          status: employee.approved
            ? MemberStatus.approved
            : employee.requested_more
            ? MemberStatus.pending
            : MemberStatus.new,
          profile: formatMessage({ id: "sharedScreen.employee" }),
        }));
        setEmployees(completeEmployees);
        setFilteredEmployees(completeEmployees);
      } else {
        setErrorOnLoadingEmployees(true);
      }
    } catch (_) {
      setErrorOnLoadingEmployees(true);
    }
    setLoadingEmployees(false);
  };

  const loadEmployee = async (id: number) => {
    setSelectedEmployeeId(id);
    setLoadingEmployee(true);
    setErrorOnLoadingEmployee(false);
    setSelectedEmployee(null);
    try {
      const employeeResult = await BranchManagerService.getEmployeeDetail({
        employeeId: id,
      });
      if (employeeResult.isSuccess && employeeResult.data.code === 100) {
        const responseData = JSON.parse(
          JSON.stringify(employeeResult.data.data)
        );
        let employee = responseData.employee;
        setSelectedEmployee({
          ...employee,
          status: employee.approved
            ? MemberStatus.approved
            : employee.requested_more
            ? MemberStatus.pending
            : MemberStatus.new,
        });
      } else {
        setErrorOnLoadingEmployee(true);
      }
    } catch (_) {
      setErrorOnLoadingEmployee(true);
    }
    setLoadingEmployee(false);
  };

  useEffect(() => {
    loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch]);

  return (
    <>
      {inviteModalOpened && (
        <InviteEmployee
          closeCallback={toggleInviteModal}
          isOpened={inviteModalOpened}
          branchId={branch}
        />
      )}
      <Grid container>
        <Grid item className={classes.leftSide} xs={4} lg={4}>
          <Grid container item xs={12} lg={12}>
            <Grid container alignItems="center" item xs={8} lg={8}>
              <FullWidthTextFieldWithSuffixIcon
                value={employeeName}
                label={formatMessage({ id: "sharedScreen.filterTeamName" })}
                icon={
                  loadingEmployees ? (
                    <CircularProgress size={25} />
                  ) : (
                    <SearchIcon />
                  )
                }
                onChange={handleEmployeeNameChange}
                onIconClick={null}
              />
            </Grid>
            <Grid container alignItems="center" item xs={4} lg={4}>
              <Button
                color="secondary"
                variant="contained"
                className={classes.newButton}
                onClick={toggleInviteModal}
              >
                <FormattedMessage id="sharedScreen.inviteAnEmployee" />
              </Button>
            </Grid>
          </Grid>
          {errorOnLoadingEmployees && (
            <Grid item xs={12} lg={12}>
              <Alert
                severity="error"
                action={
                  <Button onClick={loadEmployees} color="inherit" size="small">
                    <FormattedMessage id="sharedScreen.loadAgain" />
                  </Button>
                }
              >
                <FormattedMessage id="sharedScreen.thereWasAProblemLoadingTeamMembers" />
              </Alert>
            </Grid>
          )}
          {filteredEmployees.map((employee) => (
            <TeamMemberSearchResultItem
              {...employee}
              selected={employee.id === selectedEmployeeId}
              onClick={() => loadEmployee(employee.id)}
            />
          ))}
        </Grid>
        <Grid
          container
          item
          className={classes.rightSide}
          xs={8}
          lg={8}
          direction="row"
          justify="center"
        >
          <Grid item xs={8} lg={8}>
            {selectedEmployeeId !== null && selectedEmployee === null && (
              <Paper className={classes.rightSideMainPart}>
                {loadingEmployee && (
                  <Grid
                    container
                    justify="center"
                    alignItems="center"
                    className={classes.rightSideMainGrid}
                    item
                    xs={12}
                    lg={12}
                  >
                    <CircularProgress size={50} />
                  </Grid>
                )}
                {errorOnLoadingEmployee && (
                  <Grid
                    container
                    justify="center"
                    alignItems="center"
                    className={classes.rightSideMainGrid}
                    item
                    xs={12}
                    lg={12}
                  >
                    <Alert
                      severity="error"
                      action={
                        <Button
                          onClick={() => loadEmployee(selectedEmployeeId)}
                          color="inherit"
                          size="small"
                        >
                          <FormattedMessage id="sharedScreen.loadAgain" />
                        </Button>
                      }
                    >
                      <FormattedMessage id="sharedScreen.thereWasAProblemLoadingTheTeamMember" />
                    </Alert>
                  </Grid>
                )}
              </Paper>
            )}
            {selectedEmployee != null && (
              <Paper className={classes.rightSideMainPart}>
                <EmployeeRecord
                  {...selectedEmployee}
                  loadEmployees={loadEmployees}
                />
              </Paper>
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default MyTeamTab;
